import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Center, Collapsible, Dialog, HStack, IconButton, Menu, Portal, Separator, Spinner, Stack, StackProps, Text, useCollapsibleContext, VStack } from '@chakra-ui/react';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { GroupRequest } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';
import { LuArrowRight, LuChevronDown, LuListTodo, LuPencil, LuSettings, LuTrash2 } from 'react-icons/lu';
export const USER_GROUP_REQUEST_GOALS_QUERY = gql`
  query UserGroupRequestGoalsList($requestingUserId: String, $page: Int, $pageSize: Int, $order: [[String]]) {
    groupRequests(requestingUserId: $requestingUserId, page: $page, pageSize: $pageSize, order: $order) {
      id
      pagination {
        itemCount
      }
      items {
        id
        groupRequestId
        title
        displayTitle
        generatedTitle
        groupRequestTypeKey
        groupRequestType {
          id
          key
          title
          colorHexCode
        }
      }
    }
  }
`;
const DELETE_GROUP_REQUEST_MUTATION = gql`
  mutation UserGroupRequestGoalsListDelete($input: UpsertGroupRequestInput!) {
    upsertGroupRequest(input: $input) {
      groupRequest {
        id
        groupRequestId
        deletedAt
      }
    }
  }
`;
export type UserGroupRequestGoalsListProps = {
  /** Max goals to load (default 25). */
  pageSize?: number;
  /** Called when the query finishes with item count (for parent layout decisions). */
  onCountChange?: (count: number) => void;
  containerProps?: StackProps;
  /** Hide the list entirely when there are no goals (default true). */
  hideWhenEmpty?: boolean;
  /** Compact row style for embedding above intake. */
  compact?: boolean;
  ctaLabel?: string;
  /**
   * When set and the list has items, render a centered divider label after the
   * list (e.g. above chat intake: "or start a new goal").
   */
  startNewGoalDividerLabel?: string;
};

/**
 * Logged-in user's GroupRequest goals as list rows:
 * [Badge: type] {title} {gear menu} {Continue CTA}
 *
 * Shows the most recent goal by default. Additional goals sit in a Collapsible
 * behind "Show all Goals".
 */
export const UserGroupRequestGoalsList: React.FC<UserGroupRequestGoalsListProps> = t0 => {
  const $ = _c(27);
  const {
    pageSize: t1,
    onCountChange,
    containerProps,
    hideWhenEmpty: t2,
    compact: t3,
    ctaLabel: t4,
    startNewGoalDividerLabel
  } = t0;
  const pageSize = t1 === undefined ? 25 : t1;
  const hideWhenEmpty = t2 === undefined ? true : t2;
  const compact = t3 === undefined ? false : t3;
  const ctaLabel = t4 === undefined ? "Continue" : t4;
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const userId = currentUser?.userId;
  const t5 = !isRegistered || !userId;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [["createdAt", "desc"]];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  let t7;
  if ($[1] !== pageSize || $[2] !== userId) {
    t7 = {
      requestingUserId: userId,
      page: 1,
      pageSize,
      order: t6
    };
    $[1] = pageSize;
    $[2] = userId;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== t5 || $[5] !== t7) {
    t8 = {
      skip: t5,
      fetchPolicy: "cache-and-network",
      variables: t7
    };
    $[4] = t5;
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const query = useQuery(USER_GROUP_REQUEST_GOALS_QUERY, t8);
  let t9;
  if ($[7] !== query.data?.groupRequests?.items) {
    t9 = query.data?.groupRequests?.items || [];
    $[7] = query.data?.groupRequests?.items;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t9) {
    t10 = t9.filter(_temp);
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const items = t10;
  const itemCount = query.data?.groupRequests?.pagination?.itemCount ?? items.length;
  let t11;
  let t12;
  if ($[11] !== itemCount || $[12] !== onCountChange || $[13] !== query.data) {
    t11 = () => {
      if (onCountChange && query.data) {
        onCountChange(itemCount);
      }
    };
    t12 = [itemCount, onCountChange, query.data];
    $[11] = itemCount;
    $[12] = onCountChange;
    $[13] = query.data;
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  React.useEffect(t11, t12);
  let t13;
  if ($[16] !== query) {
    t13 = async () => {
      await query.refetch();
    };
    $[16] = query;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const reloadList = t13;
  if (!isRegistered || currentUserLoading) {
    return null;
  }
  if (query.loading && !query.data) {
    let t14;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Center py={4} width="full"><Spinner size="sm" /></Center>;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    return t14;
  }
  if (items.length === 0) {
    if (hideWhenEmpty) {
      return null;
    }
    let t14;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Text color="fg.muted" fontSize="sm" textAlign="center" width="full">No goals yet.</Text>;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    return t14;
  }
  let t14;
  if ($[20] !== compact || $[21] !== containerProps || $[22] !== ctaLabel || $[23] !== items || $[24] !== reloadList || $[25] !== startNewGoalDividerLabel) {
    t14 = <UserGroupRequestGoalsListView compact={compact} containerProps={containerProps} ctaLabel={ctaLabel} items={items} startNewGoalDividerLabel={startNewGoalDividerLabel} onDeleted={reloadList} />;
    $[20] = compact;
    $[21] = containerProps;
    $[22] = ctaLabel;
    $[23] = items;
    $[24] = reloadList;
    $[25] = startNewGoalDividerLabel;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
};
export type UserGroupRequestGoalsListViewProps = {
  items: GroupRequest[];
  compact?: boolean;
  ctaLabel?: string;
  onDeleted: () => void | Promise<void>;
  startNewGoalDividerLabel?: string;
  containerProps?: StackProps;
};

/** Presentational goals list: latest goal visible, older goals behind Collapsible. */
export const UserGroupRequestGoalsListView: React.FC<UserGroupRequestGoalsListViewProps> = t0 => {
  const $ = _c(35);
  const {
    items,
    compact: t1,
    ctaLabel: t2,
    onDeleted,
    startNewGoalDividerLabel,
    containerProps
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const ctaLabel = t2 === undefined ? "Continue" : t2;
  const listGap = compact ? 2 : 3;
  const [allGoalsOpen, setAllGoalsOpen] = useState(false);
  let T0;
  let t10;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== allGoalsOpen || $[1] !== compact || $[2] !== containerProps || $[3] !== ctaLabel || $[4] !== items || $[5] !== listGap || $[6] !== onDeleted) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const [latestGoal, ...olderGoals] = items;
      const hasOlderGoals = olderGoals.length > 0;
      if (!latestGoal) {
        t10 = null;
        break bb0;
      }
      let t11;
      if ($[16] !== compact || $[17] !== ctaLabel || $[18] !== onDeleted) {
        t11 = request => <UserGroupRequestGoalsListItem key={request.groupRequestId} compact={compact} ctaLabel={ctaLabel} request={request} onDeleted={onDeleted} />;
        $[16] = compact;
        $[17] = ctaLabel;
        $[18] = onDeleted;
        $[19] = t11;
      } else {
        t11 = $[19];
      }
      const renderItem = t11;
      T0 = VStack;
      t3 = "stretch";
      t4 = listGap;
      t5 = "left";
      t6 = "full";
      t7 = containerProps;
      if ($[20] !== latestGoal || $[21] !== renderItem) {
        t8 = renderItem(latestGoal);
        $[20] = latestGoal;
        $[21] = renderItem;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      t9 = hasOlderGoals && <Collapsible.Root open={allGoalsOpen} width="full" onOpenChange={details => {
        setAllGoalsOpen(details.open);
      }}><Collapsible.Content><VStack align="stretch" gap={listGap} paddingBottom={listGap} width="full">{olderGoals.map(renderItem)}</VStack></Collapsible.Content><Center width="full"><ShowAllGoalsButton compact={compact} /></Center></Collapsible.Root>;
    }
    $[0] = allGoalsOpen;
    $[1] = compact;
    $[2] = containerProps;
    $[3] = ctaLabel;
    $[4] = items;
    $[5] = listGap;
    $[6] = onDeleted;
    $[7] = T0;
    $[8] = t10;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[7];
    t10 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[23] !== startNewGoalDividerLabel) {
    t11 = !!startNewGoalDividerLabel && <HStack gap={4} paddingTop={3} width="full"><Separator flex={1} /><Text color="fg.muted" flexShrink={0} fontSize="sm" fontWeight="medium" textAlign="center">{startNewGoalDividerLabel}</Text><Separator flex={1} /></HStack>;
    $[23] = startNewGoalDividerLabel;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== T0 || $[26] !== t11 || $[27] !== t3 || $[28] !== t4 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t12 = <T0 align={t3} gap={t4} textAlign={t5} width={t6} {...t7}>{t8}{t9}{t11}</T0>;
    $[25] = T0;
    $[26] = t11;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  return t12;
};
const ShowAllGoalsButton: React.FC<{
  compact?: boolean;
}> = t0 => {
  const $ = _c(4);
  const {
    compact: t1
  } = t0;
  t1 === undefined ? false : t1;
  const collapsible = useCollapsibleContext();
  const t2 = collapsible.open ? "Show fewer" : "Show all Goals";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      rotate: "180deg"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Collapsible.Indicator _open={t3} transition="rotate 0.2s"><LuChevronDown /></Collapsible.Indicator>;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t2) {
    t5 = <Collapsible.Trigger asChild={true}><Button color="fg.muted" size="xs" variant="ghost">{t2}{t4}</Button></Collapsible.Trigger>;
    $[2] = t2;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  return t5;
};
type UserGroupRequestGoalsListItemProps = {
  request: GroupRequest;
  compact?: boolean;
  ctaLabel: string;
  onDeleted: () => void | Promise<void>;
};
const UserGroupRequestGoalsListItem: React.FC<UserGroupRequestGoalsListItemProps> = ({
  request,
  compact = false,
  ctaLabel,
  onDeleted
}) => {
  const router = useRouter();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const isNarrow = getBreakpointValue([true, true, true, false]);
  const cardPaddingX = getBreakpointValue(compact ? [3, 3, 3] : [3, 3, 4]);
  const cardPaddingY = getBreakpointValue(compact ? [2.5, 2.5, 2.5] : [3, 3, 3.5]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteGroupRequest, deleteMutation] = useMutation(DELETE_GROUP_REQUEST_MUTATION);
  const groupRequestId = request.groupRequestId as string;
  const typeTitle = request.groupRequestType?.title || request.groupRequestTypeKey || 'Goal';
  const typeColor = request.groupRequestType?.colorHexCode;
  const title = request.displayTitle || request.title || request.generatedTitle || 'Untitled goal';
  const manageHref = `/requests/manage/${groupRequestId}`;
  const gamePlanHref = `/requests/manage/${groupRequestId}/tasks`;
  const editHref = `/requests/manage/${groupRequestId}/edit`;
  const handleDelete = async () => {
    setDeleteError(null);
    try {
      const res = await deleteGroupRequest({
        variables: {
          input: {
            groupRequestId,
            deletedAt: new Date()
          }
        }
      });
      if (res.error) {
        throw new Error(res.error.message || 'Could not delete goal');
      }
      setDeleteOpen(false);
      await onDeleted();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete goal');
    }
  };
  const typeBadge = <Badge bgColor={typeColor || 'colorPalette.muted'} color={typeColor ? 'white' : 'colorPalette.fg'} flexShrink={0} size={'xs'} variant={'label'}>
      {typeTitle}
    </Badge>;
  const actions = <HStack flexShrink={0} gap={1}>
      <Menu.Root positioning={{
      placement: 'bottom-end'
    }}>
        <Menu.Trigger asChild>
          <IconButton aria-label={`Goal options for ${title}`} size={compact || isNarrow ? 'sm' : 'md'} variant={'ghost'}>
            <LuSettings />
          </IconButton>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content minW={'11rem'}>
              <Menu.Item value={'game-plan'} onClick={() => {
              void router.push(gamePlanHref);
            }}>
                <LuListTodo />
                View Game Plan
              </Menu.Item>
              <Menu.Item value={'edit'} onClick={() => {
              void router.push(editHref);
            }}>
                <LuPencil />
                Edit
              </Menu.Item>
              <Menu.Separator />
              <Menu.Item color={'fg.error'} value={'delete'} onClick={() => {
              setDeleteError(null);
              setDeleteOpen(true);
            }}>
                <LuTrash2 />
                Delete
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>

      <ButtonLink href={manageHref} size={compact || isNarrow ? 'sm' : 'md'} variant={'outlineGhost'}>
        {ctaLabel} <LuArrowRight />
      </ButtonLink>
    </HStack>;
  return <>
      <Stack align={isNarrow ? 'stretch' : 'center'} bg={'colorPalette.card/50'} borderColor={'colorPalette.border'} borderRadius={'xl'} borderWidth={'1px'} direction={isNarrow ? 'column' : 'row'} gap={isNarrow ? 2 : 2} justify={'space-between'} paddingX={cardPaddingX} paddingY={cardPaddingY} transition={'background-color 120ms ease, border-color 120ms ease'} width={'full'} _hover={{
      borderColor: 'colorPalette.border',
      bg: 'colorPalette.card/75'
    }}>
        {isNarrow ? <>
            <HStack align={'center'} justify={'space-between'} width={'full'}>
              {typeBadge}
              {actions}
            </HStack>
            <Box asChild _hover={{
          textDecoration: 'none'
        }} color={'inherit'} minWidth={0} textDecoration={'none'}>
              <NextLink href={manageHref}>
                <Text cursor={'pointer'} fontSize={'sm'} fontWeight={'medium'} lineClamp={2} textAlign={'left'}>
                  {title}
                </Text>
              </NextLink>
            </Box>
          </> : <>
            <Box asChild _hover={{
          textDecoration: 'none'
        }} color={'inherit'} flex={1} minWidth={0} textDecoration={'none'}>
              <NextLink href={manageHref}>
                <HStack align={'center'} cursor={'pointer'} gap={3} minWidth={0} width={'full'}>
                  {typeBadge}
                  <Text flex={1} fontSize={'sm'} fontWeight={'medium'} lineClamp={1} minWidth={0}>
                    {title}
                  </Text>
                </HStack>
              </NextLink>
            </Box>
            {actions}
          </>}
      </Stack>

      <Dialog.Root open={deleteOpen} placement={'center'} role={'alertdialog'} onOpenChange={({
      open
    }) => {
      if (!deleteMutation.loading) {
        setDeleteOpen(open);
        if (!open) {
          setDeleteError(null);
        }
      }
    }}>
        <Portal>
          <Dialog.Backdrop backdropFilter={'blur(4px)'} bg={'blackAlpha.700'} />
          <Dialog.Positioner paddingX={4}>
            <Dialog.Content bgColor={'colorPalette.container'} borderColor={'colorPalette.border'} borderRadius={'2xl'} borderWidth={'1px'} maxWidth={'28em'} padding={6} width={'full'}>
              <VStack align={'stretch'} gap={4}>
                <Dialog.Header padding={0}>
                  <Dialog.Title fontSize={'lg'} fontWeight={'semibold'}>
                    Delete this goal?
                  </Dialog.Title>
                </Dialog.Header>
                <Dialog.Body padding={0}>
                  <Text color={'fg.muted'} fontSize={'sm'}>
                    Deleting “{title}” will also clear its game plan, matches, and related activity. This
                    cannot be undone.
                  </Text>
                  {deleteError && <Text color={'fg.error'} fontSize={'sm'} marginTop={3}>
                      {deleteError}
                    </Text>}
                </Dialog.Body>
                <Dialog.Footer display={'flex'} gap={3} justifyContent={'flex-end'} padding={0}>
                  <Button disabled={deleteMutation.loading} variant={'outline'} onClick={() => setDeleteOpen(false)}>
                    Cancel
                  </Button>
                  <Button colorPalette={'red'} loading={deleteMutation.loading} loadingText={'Deleting…'} variant={'solid'} onClick={() => {
                  void handleDelete();
                }}>
                    Delete
                  </Button>
                </Dialog.Footer>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>;
};
export type UseUserGroupRequestGoalsResult = {
  items: GroupRequest[];
  itemCount: number;
  loading: boolean;
  error?: Error;
};

/** Hook for manage-index redirect / selector logic (same query shape as the list). */
export function useUserGroupRequestGoals(options) {
  const $ = _c(19);
  const {
    currentUser,
    currentUserLoading
  } = useCurrentUser();
  const isRegistered = !!currentUser?.isRegistered;
  const userId = currentUser?.userId;
  const t0 = options?.skip || !isRegistered || !userId;
  const t1 = options?.pageSize ?? 25;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [["createdAt", "desc"]];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1 || $[2] !== userId) {
    t3 = {
      requestingUserId: userId,
      page: 1,
      pageSize: t1,
      order: t2
    };
    $[1] = t1;
    $[2] = userId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t0 || $[5] !== t3) {
    t4 = {
      skip: t0,
      fetchPolicy: "network-only",
      variables: t3
    };
    $[4] = t0;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const query = useQuery(USER_GROUP_REQUEST_GOALS_QUERY, t4);
  let t5;
  if ($[7] !== query.data?.groupRequests?.items) {
    t5 = query.data?.groupRequests?.items || [];
    $[7] = query.data?.groupRequests?.items;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = t5.filter(_temp2);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const items = t6;
  const itemCount = query.data?.groupRequests?.pagination?.itemCount ?? items.length;
  const t7 = currentUserLoading || !!isRegistered && query.loading && !query.data;
  const t8 = !currentUserLoading && (!isRegistered || !!query.data || !!query.error);
  const t9 = query.error as Error | undefined;
  let t10;
  if ($[11] !== currentUser || $[12] !== isRegistered || $[13] !== itemCount || $[14] !== items || $[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = {
      items,
      itemCount,
      loading: t7,
      ready: t8,
      isRegistered,
      error: t9,
      currentUser
    };
    $[11] = currentUser;
    $[12] = isRegistered;
    $[13] = itemCount;
    $[14] = items;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp2(item) {
  return !!item?.groupRequestId;
}
export default UserGroupRequestGoalsList;
function _temp(item) {
  return !!item?.groupRequestId;
}